import React from 'react';
import { FormHelperText, Stack } from '@mui/material';
import { CampaignPolicy } from '../../policies';
import { CampaignConfigSchema } from '../../types';
import { FieldKey } from '../../types';
import {
  CampaignNameField,
  RecipientsField,
  KeywordsField,
  MessagesField,
  RecurringSettingsField,
  JoinWindowField,
  AppointmentTypeField,
  ContactDateTypeField,
  OptOutField,
  TextToWinSettingsField,
  TextToWinWinnersField,
} from '../fields';
import { useCampaignFormContext } from '../../context';

interface Props {
  policy: CampaignPolicy;
  config: CampaignConfigSchema;
}

export const PolicyFields: React.FC<Props> = ({ policy, config }) => {
  const manifest = policy.fieldManifest(config);
  const { errors } = useCampaignFormContext();

  // A pathless schema rule lands on `root`. Every one shipped today is ALSO shown
  // against its own control — message order under the offending picker, recurring
  // and prize-draw settings inside their own forms. So this is a last-resort net
  // for a rule with nowhere else to appear: rendered only when nothing else is on
  // screen, rather than repeating a message the user is already reading.
  const rootError = Object.keys(errors).length === 1 ? errors.root : undefined;

  return (
    <Stack spacing={0}>
      {manifest.map((key: FieldKey) => {
        switch (key) {
          case 'name': return <CampaignNameField key={key} />;
          case 'recipients': return <RecipientsField key={key} />;
          case 'keywords': return <KeywordsField key={key} />;
          case 'appointmentType': return <AppointmentTypeField key={key} />;
          case 'contactDateType': return <ContactDateTypeField key={key} />;
          case 'joinWindow': return <JoinWindowField key={key} />;
          case 'messages': return <MessagesField key={key} policy={policy} config={config} />;
          case 'recurringSettings': return <RecurringSettingsField key={key} />;
          case 'textToWinWinners': return <TextToWinWinnersField key={key} />;
          case 'textToWinSettings': return <TextToWinSettingsField key={key} />;
          case 'optOut': return <OptOutField key={key} />;
          default: {
            return null;
          }
        }
      })}
      {rootError && (
        <FormHelperText error role="alert" sx={{ marginLeft: 0, marginBottom: '10px' }}>
          {rootError}
        </FormHelperText>
      )}
    </Stack>
  );
};
